// On + arka tek liste: analizin tasarimlarindan biri on, biri arka secilip baglanir.
// On tasarim listelenir; mockuplardaki "arka/back" alanlarina arka tasarim basilir.
// Arka tasarim Liste Denetimi'nde ayri liste olarak gorunmez.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ArrowLeftRight, ChevronDown, ChevronUp, Link2, Loader2, Unlink } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

export interface LinkRow { analysisDesignId: string; sku: string | null; url: string; poolDesignId: string | null; backOf: string | null; backId: string | null; inSet: boolean }

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, { method, headers: authHeaders(), ...(body ? { body: JSON.stringify(body) } : {}) });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error ?? `İstek başarısız (${r.status})`);
  return j.data as T;
}

export function useDesignLinks(analysisId: string) {
  return useQuery({ queryKey: ["design-links", analysisId], queryFn: () => call<LinkRow[]>("GET", `/${analysisId}/design-links`) });
}

export function FrontBackPanel({ analysisId }: { analysisId: string }) {
  const [open, setOpen] = useState(false);
  const [front, setFront] = useState<string | null>(null);
  const [back, setBack] = useState<string | null>(null);
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useDesignLinks(analysisId);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["design-links", analysisId] });
  const pair = useMutation({ mutationFn: () => call("POST", `/${analysisId}/design-links/pair`, { frontId: front, backId: back }), onSuccess: () => { setFront(null); setBack(null); refresh(); } });
  const unpair = useMutation({ mutationFn: (frontId: string) => call("POST", `/${analysisId}/design-links/unpair`, { frontId }), onSuccess: refresh });

  const byPool = new Map(rows.map((r) => [r.poolDesignId, r]));
  const pairs = rows.filter((r) => r.backId).map((r) => ({ front: r, back: byPool.get(r.backId) }));
  const free = rows.filter((r) => !r.backId && !r.backOf && !r.inSet);
  const pick = (id: string) => {
    if (front === id) { setFront(null); return; }
    if (back === id) { setBack(null); return; }
    if (!front) setFront(id); else setBack(id);
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center justify-between gap-2 text-left">
        <span className="flex items-center gap-2">
          <ArrowLeftRight className="h-4 w-4 text-indigo-500" />
          <span className="text-base font-semibold text-gray-900">Ön + arka (tek liste)</span>
          {pairs.length > 0 && <span className="text-xs text-gray-500">{pairs.length} çift bağlı</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">Listede ön ve arka baskı ayrı tasarımlarsa burada bağla: önce ön, sonra arka tasarıma tıkla. Liste ön tasarımla açılır; mockup'larda "arka/back" etiketli alanlara arka tasarım basılır. Arka tasarım Liste Denetimi'nde ayrı liste olarak çıkmaz. Grup için (her biri ayrı tişört) tasarım setini kullan.</p>
          {pairs.length > 0 && (
            <div className="space-y-2">
              {pairs.map(({ front: f, back: b }) => (
                <div key={f.analysisDesignId} className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 p-2">
                  <figure className="text-center"><img src={f.url} alt="" className="h-16 w-16 rounded border border-gray-200 bg-white object-contain" /><figcaption className="text-[11px] text-gray-500">Ön · {f.sku}</figcaption></figure>
                  <Link2 className="h-4 w-4 text-gray-400" />
                  {b && <figure className="text-center"><img src={b.url} alt="" className="h-16 w-16 rounded border border-gray-200 bg-white object-contain" /><figcaption className="text-[11px] text-gray-500">Arka · {b.sku}</figcaption></figure>}
                  <button onClick={() => unpair.mutate(f.analysisDesignId)} disabled={unpair.isPending} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600">
                    <Unlink className="h-3.5 w-3.5" /> Ayır
                  </button>
                </div>
              ))}
            </div>
          )}
          {isLoading ? <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Tasarımlar yükleniyor…</p>
            : free.length < 2 ? <p className="text-sm text-gray-500">Bağlamak için bu analizde en az iki serbest tasarım olmalı.</p>
            : (
              <>
                <div className="flex flex-wrap gap-2">
                  {free.map((r) => {
                    const role = front === r.analysisDesignId ? "Ön" : back === r.analysisDesignId ? "Arka" : null;
                    return (
                      <button key={r.analysisDesignId} onClick={() => pick(r.analysisDesignId)} aria-pressed={!!role} title={r.sku ?? ""}
                        className={clsx("relative h-20 w-20 overflow-hidden rounded-lg border-2 bg-gray-50", role ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
                        <img src={r.url} alt="" loading="lazy" className="h-full w-full object-contain" />
                        {role && <span className="absolute left-1 top-1 rounded bg-indigo-600 px-1 text-[10px] font-semibold text-white">{role}</span>}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => pair.mutate()} disabled={!front || !back || pair.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                  {pair.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Ön + arkayı bağla
                </button>
              </>
            )}
          {(pair.error || unpair.error) && <p className="text-sm text-red-600">{((pair.error || unpair.error) as Error).message}</p>}
        </div>
      )}
    </section>
  );
}
